import { Component, Input, OnChanges, OnInit, ViewChild, ElementRef, Output, EventEmitter, OnDestroy } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
    ComplexConfiguratorField,
    DiscountsAndMultipliersService,
    SalesDocument,
    DocumentAttachment,
    SalesDocumentPosition,
    TaxRate,
    TaxRatesService,
    SalesDocumentType,
    StaticPositionDetailsService,
    Synced,
} from '@icc/b2b/shared';
import { ExchangeRate, ExchangeRateService } from '@icc/shared/currency';
import { DialogService } from '@icc/ui';
import { ChangeUnitDialogComponent } from '../change-unit-dialog/change-unit-dialog.component';
import { SalesDocumentViewMode } from '../models/view-modes.model';
import { PositionPricesDialogComponent } from '../position-prices-dialog/position-prices-dialog.component';
import { PositionService } from '../position.service';
import { SalesDocumentService } from '../sales-document.service';
import { core, DetailsTreeNode, ToastService } from '@icc/shared/helpers/b2b';
import { TranslateService } from '@ngx-translate/core';
import { PriceVersionService } from '../price-version-selector/price-version.service';
import { ConfigService, PricesDataService } from '@icc/shared';
import { getUnit, logger, unitMMFormatter, _ } from '@icc/shared/helpers';
import { OfferValidityService } from '../offer-validity/offer-validity.service';
import { sumPriceFromSegments } from '@icc/price2';
import { NgxPermissionsService } from 'ngx-permissions';
import { Subscription } from 'rxjs';
import { DocumentManagerService } from '../document-manager.service';
import { DocumentSlavePositionsService } from '../document-slave-positions.service';
import { DocumentPricesService } from '../document-prices.service';
import { ConfiguratorLoaderService } from '@icc/b2b/configurator';
import { ConfiguratorsDataService } from '@icc/configurator/shared';
import { BreakpointObserver } from '@angular/cdk/layout';
import { PositionAction } from './position-action';

@Component({
    selector: 'icc-sales-document-position',
    templateUrl: './sales-document-position.component.html',
    styleUrls: ['./sales-document-position.component.scss'],
})
export class SalesDocumentPositionComponent implements OnInit, OnChanges, OnDestroy {
    private _position: SalesDocumentPosition;

    @ViewChild('quantityInput') quantityInput!: ElementRef<HTMLInputElement>;
    @Input() sorting = false;
    @Input() index = 0;
    @Input()
    get position(): SalesDocumentPosition{
        return this._position;
    }
    set position(position:SalesDocumentPosition){
        this._position = position;
        this.parseSketches();
    }
    @Input() salesPosition: SalesDocumentPosition | null;
    @Input() buyPosition: SalesDocumentPosition | null;
    @Input() positionTree: DetailsTreeNode = {
        $$children: []
    };
    @Input() showDetails = false;
    @Input() positionTitle: string[] = [];
    @Input() openedDocument: SalesDocument;
    @Input() document: SalesDocument;
    @Input() attachments: DocumentAttachment[];
    @Input() currency: string;
    @Input() viewMode: SalesDocumentViewMode = 'sale';
    @Input() positionsIndexes: Map<string, string> = new Map();
    @Input() positionsTitles = new Map<string, string[]>();
    @Input() deliveryConfirmed = false;
    @Input() hostname = window.location.origin;
    @Input() synced: Synced = Synced.Offline;
    @Input() missingBuyDocument = false;

    @Output() showDetailsEvent = new EventEmitter<boolean>();
    @Output() deliveredChange = new EventEmitter<boolean>();

    tempPriceUnit: any = {};
    priceBase = 0;
    priceBaseBeforePromotion = 0;
    priceBasePromotion = 0;
    priceSurcharges = 0;
    priceSurchargesBeforePromotion = 0;
    priceSurchargesPromotion = 0;
    buyPriceBase = 0;
    buyPriceBaseBeforePromotion = 0;
    buyPriceBasePromotion = 0;
    buyPriceSurcharges = 0;
    buyPriceSurchargesBeforePromotion = 0;
    buyPriceSurchargesPromotion = 0;
    discountValue = 0;
    discountPercent = 0;
    discountPercentControl = new UntypedFormControl();
    buyDiscountValue = 0;
    buyDiscountPercent = 0;
    editUnit = false;
    matchingPrices = null;
    newUnit = null;
    oldQuantity = 1;
    newQuantity = 1;
    hasPrice = false;
    hasBuyPrice = false;

    taxRates: TaxRate[] = [];
    buyExchangeRates: ExchangeRate[] = [];
    activePriceVersion: any;
    hasAccessTo = {
        positionName: false,
        positionPrices: false,
        copy: false,
        copyToDocument: false,
        edit: false,
        remove: false,
        quantity: false,
        unit: false,
        description: false,
        attachments: false,
        taxRate: false,
        viewBuyPrices: false,
        addSpareParts: false,
        commitDelivery: false,
        requestComplaint: false,
    };

    actions: PositionAction[] = [
        {
          action: () => this.editPosition(),
          icon: 'edit',
          when: () => this.hasAccessTo.edit,
          label: _('INTERFACE|Edytuj')
        },
        {
          action: () => this.duplicatePosition(),
          icon: 'copy',
          when: () => this.hasAccessTo.copy,
          label: _('SALES_DOCUMENT|Skopiuj pozycję')
        },
        {
          action: () => this.copyPositionToOtherDocument(),
          icon: 'copy_out',
          when: () => this.hasAccessTo.copyToDocument,
          label: _('SALES_DOCUMENT|Skopiuj pozycję do innej oferty')
        },
        {
          action: () => this.addSpareParts(),
          icon: 'spare_parts_copy_out',
          when: () => this.hasAccessTo.addSpareParts,
          label: _('SALES_DOCUMENT|Części zamienne / dobór do kompletu')
        },
        {
          action: () => this.requestComplaint(),
          icon: 'comment',
          when: () => this.hasAccessTo.requestComplaint,
          label: _('COMPLAINT|Zgłoś reklamację')
        },
        {
          action: () => this.removePosition(),
          icon: 'delete',
          when: () => this.hasAccessTo.remove,
          disabled: () => this.synced !== Synced.Synced,
          label: _('INTERFACE|Usuń')
        },
      ];

    viewBox: {
        viewBox: {x: number, y:number, height: number, width: number},
        originBox: {x: number, y:number, height: number, width: number},
    };

    drawOptions = {
        base: {
            indexes: true,
            ventilator: this.configService.config.config?.Drawing?.ventilator,
            side: 'inner',
            showSymbols: true
        },
        colors: {
            glassStartColor: this.configService.config.config?.Drawing?.glassStartColor,
            glassStopColor: this.configService.config.config?.Drawing?.glassStopColor,
            glassGradient: this.configService.config.config?.Drawing?.glassGradient,
        },
        indexes: true,
        dimensions: {
            multiplier: 1,
        },
        maxHeight: 450,
        texts: {
            angle90degIn: this.translateService.instant('EXTERNAL_VENETIAN_DRAW|Wewn'),
            angle90degOut: this.translateService.instant('EXTERNAL_VENETIAN_DRAW|Zewn'),
            angle90: this.translateService.instant('EXTERNAL_VENETIAN_DRAW|90°'),
        },
    }

    thumbnailDrawOptions = {
        base: {
            indexes: true,
            ventilator: this.configService.config.config?.Drawing?.ventilator,
            side: 'inner',
            showSymbols: true
        },
        colors: {
            glassStartColor: this.configService.config.config?.Drawing?.glassStartColor,
            glassStopColor: this.configService.config.config?.Drawing?.glassStopColor,
            glassGradient: this.configService.config.config?.Drawing?.glassGradient,
        },
        indexes: true,
        dimensions: false,
        shutter: {
            multiplier: 0.7
        },
        height: '120px'
    };

    thumbnailTopProjectionDrawOptions = {
        base: {
            indexes: true,
            ventilator: this.configService.config.config?.Drawing?.ventilator,
            side: 'inner',
            showSymbols: true,
            type: 'top-projection'
        },
        colors: {
            glassStartColor: this.configService.config.config?.Drawing?.glassStartColor,
            glassStopColor: this.configService.config.config?.Drawing?.glassStopColor,
            glassGradient: this.configService.config.config?.Drawing?.glassGradient,
        },
        indexes: true,
        dimensions: false,
        shutter: {
            multiplier: 0.7
        },
        height: '120px'
    };

    thumbnailDrawType = 'normal';
    showDraws = false;

    showPercentDiscountInPosition = true;

    mobileView = false;

    private subscriptions: Subscription[] = [];

    constructor(
        private configService: ConfigService,
        private positionService: PositionService,
        private router: Router,
        private salesDocumentService: SalesDocumentService,
        private taxRatesService: TaxRatesService,
        private dialogService: DialogService,
        private priceVersionService: PriceVersionService,
        private pricesDataService: PricesDataService,
        private discountsAndMultipliersService: DiscountsAndMultipliersService,
        private exchangeRateService: ExchangeRateService,
        private translateService: TranslateService,
        private toastService: ToastService,
        private offerValidityService: OfferValidityService,
        private staticPositionDetailsService: StaticPositionDetailsService,
        private permissionsService: NgxPermissionsService,
        private documentManagerService: DocumentManagerService,
        private documentSlavePositions: DocumentSlavePositionsService,
        private documentPricesService: DocumentPricesService,
        private configuratorLoaderService: ConfiguratorLoaderService,
        private breakpointObserver: BreakpointObserver,
    ) {
        this.subscriptions.push(
            this.priceVersionService.activatedPriceVersion$.subscribe(async (priceVersion) => {
                if (!this.activePriceVersion || this.activePriceVersion.id !== priceVersion.id) {
                    this.activePriceVersion = priceVersion;
                }
            }),
            this.permissionsService.permissions$.subscribe(() => {
                this.checkAccess();
            }),
            this.breakpointObserver.observe(['(max-width: 990px)']).subscribe(result => {
                this.mobileView = result.matches;
            }),
        );
    }

    ngOnInit(): void {
        this.taxRates = this.taxRatesService.getRates();
        this.buyExchangeRates = this.exchangeRateService.buyExchangeRates;
        this.showPercentDiscountInPosition = this.configService.config.config?.Offer?.showPercentDiscountInPosition;

    }

    ngOnChanges() {
        this.summarizePrices();
        this.checkAccess();
        this.checkNeedChangeUnit();
        this.fixPositionName();

        setTimeout(() => {
            if (!this.showDraws) {
                this.showDraws = true;
            }
        }, 0);
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    }

    get saleView() {
        return this.viewMode === 'comparison' || this.viewMode === 'sale';
    }

    get purchaseView() {
        return this.buyPosition && (this.viewMode === 'comparison' || this.viewMode === 'purchase') && this.hasAccessTo.viewBuyPrices;
    }

    get isGross() {
        if (this.isComparable) {
            if (this.viewMode === 'purchase') {
                return this.buyPosition?.priceIsGross;
            } else {
                return this.position.priceIsGross;
            }
        }
        return false;
    }

    get isComparable() {
        return (
            this.viewMode !== 'comparison' ||
            (this.viewMode === 'comparison' &&
                (this.position.priceIsGross === this.buyPosition?.priceIsGross))
        );
    }

    get marginRight() {
        const coversLength = this.position?.configuration?.coverSegments?.reduce((a, b) => a + b.length + b.shift, 0);
        const shuttersLength = this.position?.configuration?.shutters?.reduce((a, b) => a + b.width, 0);
        return coversLength - shuttersLength;
    }

    async checkAccess() {
        if (this.openedDocument && (this.salesPosition || this.openedDocument?.salesDocumentTypeId === SalesDocumentType.ServiceOrder)) {
            let canManage = false;
            if (this.openedDocument.salesDocumentTypeId === SalesDocumentType.Order) {
                canManage = await this.salesDocumentService.hasAccessToOrder(
                    this.openedDocument,
                    'manage'
                );
            } else if (this.openedDocument.salesDocumentTypeId === SalesDocumentType.Inquiry) {
                canManage = await this.salesDocumentService.hasAccessToManageInquiry(
                    this.openedDocument,
                );
            } else if (this.openedDocument.salesDocumentTypeId === SalesDocumentType.ServiceOrder) {
                canManage = await this.salesDocumentService.hasAccessToManageServiceOrder(
                    this.openedDocument,
                );
            } else {
                canManage = await this.salesDocumentService.hasAccessToManageQuotation(
                    this.openedDocument
                );
            }
            const { invalidOffer } = this.offerValidityService.getValidDate(this.openedDocument);
            const canBeEdited =
                !this.openedDocument.isCanceled &&
                this.openedDocument.externalSource !== 'ERP' &&
                !this.openedDocument.isConfirmed &&
                (!this.openedDocument.isConfirmedOnlySalesPositions
                    || this.openedDocument.isConfirmedOnlySalesPositions && this.salesPosition.type === 'additional') &&
                (this.openedDocument.salesDocumentTypeId === SalesDocumentType.Order || !this.openedDocument.isOrderPlaced) &&
                canManage &&
                !this.missingBuyDocument;

            this.hasAccessTo.positionName = canBeEdited &&
                !invalidOffer &&
                this.openedDocument.salesDocumentTypeId !== SalesDocumentType.Inquiry &&
                this.openedDocument.salesDocumentTypeId !== SalesDocumentType.ServiceOrder;
            this.hasAccessTo.positionPrices = this.salesPosition.externalSource !== 'ERP' &&
                canBeEdited &&
                !invalidOffer &&
                (
                    this.openedDocument.salesDocumentTypeId === SalesDocumentType.Inquiry ||
                    this.salesPosition.priceBeforeDiscountsValue !== null &&
                    !Number.isNaN(this.salesPosition.priceBeforeDiscountsValue) &&
                    typeof this.salesPosition.priceBeforeDiscountsValue !== 'undefined' ||
                    this.salesPosition.type === 'additional'
                );
            this.hasAccessTo.copy = canBeEdited &&
                !invalidOffer &&
                this.openedDocument.salesDocumentTypeId !== SalesDocumentType.Inquiry &&
                !this.salesPosition.customPriceValue;
            this.hasAccessTo.addSpareParts =
                !this.openedDocument.isCanceled &&
                this.openedDocument.salesDocumentTypeId !== SalesDocumentType.Inquiry &&
                !this.salesPosition.customPriceValue &&
                await this.canAddSpareParts();
            this.hasAccessTo.copyToDocument = this.salesPosition.externalSource !== 'ERP' &&
                (canBeEdited &&
                    this.openedDocument.salesDocumentTypeId !== SalesDocumentType.Inquiry &&
                    this.openedDocument.salesDocumentTypeId !== SalesDocumentType.ServiceOrder &&
                    !this.salesPosition.customPriceValue ||
                    this.openedDocument.salesDocumentTypeId === SalesDocumentType.Inquiry &&
                    !this.openedDocument.isCanceled &&
                    this.openedDocument.isConfirmed &&
                    !this.openedDocument.isConfirmedOnlySalesPositions
                ) &&
                !invalidOffer;
            this.hasAccessTo.edit =  this.salesPosition.externalSource !== 'ERP' &&
                this.salesPosition.type !== 'static' &&
                    this.salesPosition.type !== 'additional'
                    && canBeEdited
                    && !invalidOffer;
            this.hasAccessTo.remove = canBeEdited &&
                !invalidOffer &&
                Object.keys(this.salesPosition?.masters || {}).length === 0 &&
                this.openedDocument.salesDocumentTypeId !== SalesDocumentType.Inquiry;
            this.hasAccessTo.quantity = canBeEdited &&
                (!invalidOffer ||
                    this.salesPosition.isInvalid &&
                    this.errorInPosition('BadUnit') &&
                    this.salesPosition?.hasMatchingPrices
                ) &&
                Object.keys(this.salesPosition?.masters || {}).length === 0 &&
                this.openedDocument.salesDocumentTypeId !== SalesDocumentType.Inquiry &&
                !this.sorting;
            this.hasAccessTo.unit = this.salesPosition.externalSource !== 'ERP' &&
                canBeEdited &&
                Object.keys(this.salesPosition?.masters || {}).length === 0 &&
                this.openedDocument.salesDocumentTypeId !== SalesDocumentType.Inquiry;
            this.hasAccessTo.description = canBeEdited &&
                !invalidOffer &&
                this.openedDocument.salesDocumentTypeId !== SalesDocumentType.Inquiry &&
                this.openedDocument.salesDocumentTypeId !== SalesDocumentType.ServiceOrder;
            this.hasAccessTo.attachments = canBeEdited &&
                !invalidOffer &&
                this.salesPosition.type !== 'static' &&
                this.openedDocument.salesDocumentTypeId !== SalesDocumentType.Inquiry &&
                this.openedDocument.salesDocumentTypeId !== SalesDocumentType.ServiceOrder;
            this.hasAccessTo.taxRate = this.salesPosition.externalSource !== 'ERP' &&
                canBeEdited &&
                !invalidOffer &&
                this.openedDocument.salesDocumentTypeId !== SalesDocumentType.Inquiry &&
                this.openedDocument.salesDocumentTypeId !== SalesDocumentType.ServiceOrder;
            this.hasAccessTo.requestComplaint = !this.salesPosition.isInvalid &&
                this.document.salesDocumentTypeId === SalesDocumentType.Order &&
                this.document.isConfirmed &&
                this.document.isOrderPlaced &&
                !this.document.isCanceled &&
                this.viewMode === 'purchase' &&
                await this.permissionsService.hasPermission('trade-request-complaint-v1');
        }
        this.hasAccessTo.commitDelivery = this.configService.config.userData?.user?.ActivePanel === 'driver' &&
                await this.permissionsService.hasPermission('driver-deliveries');
        this.hasAccessTo.viewBuyPrices = await this.permissionsService.hasPermission('purchase-prices-view');
    }

    hasAccessToAny(actions: PositionAction[]) {
        return actions.some(action => action.when());
    }

    async changeUnit(position: SalesDocumentPosition, salesDocument: SalesDocument, newPriceUnit) {
        if(newPriceUnit){
            this.salesDocumentService.changeUnitInPosition(position, salesDocument, newPriceUnit);
            this.editUnit = false;
            await this.changePositionQuantity(this.newQuantity);
            this.oldQuantity = position.quantity;
        }
    }

    editPosition() {
        const commands = ['configurator', 'edit', this.salesPosition.uuid];
        if(this.salesPosition?.configuration?.productId){
            commands.push(this.salesPosition.configuration.productId);
        } else {
            commands.push('0');
        }
        const openedDocument = this.salesDocumentService.getOpenedDocument();
        commands.push(openedDocument?.uuid);
        this.router.navigate(commands);
    }

    checkQuantity(quantity: number): number{
        quantity = +quantity;
        if(!isNaN(quantity)){
            if (this.newUnit && (this.newUnit?.symbol === 'pcs' || this.newUnit?.code === 'pcs') || !this.newUnit && (this.salesPosition?.configuration?.unit?.symbol === 'pcs' || this.salesPosition?.configuration?.unit?.code === 'pcs')) {
                let roundQuantity = Math.ceil(quantity);
                if(roundQuantity !== quantity){
                    this.clearQuantityInput(roundQuantity);
                    this.addToastWarnQuantity();
                }
                if (roundQuantity < 1) roundQuantity = 1;
                quantity = roundQuantity;
            }else{
                quantity = Math.round(quantity*10)/10;
                if (quantity < 0.1) quantity = 1;
            }
            return quantity;
        }else{
            return 1;
        }
    }

    clearQuantityInput(quantity:number){
        if(this.quantityInput?.nativeElement){
            this.quantityInput.nativeElement.value = quantity.toString();
        }
    }

    getQuantityStepAndMin(){
        if(this.position?.priceUnitCode === 'pcs'){
            return 1;
        }else{
            return 0.1;
        }
    }

    async changePositionQuantity(quantity: number) {
        if(typeof +quantity !== 'number') {
            quantity = +quantity;
        }
        quantity = this.checkQuantity(quantity);
        if (this.editUnit) {
            this.newQuantity = quantity;
            return;
        }
        this.documentManagerService.savePosition(this.salesPosition.uuid, this.openedDocument.uuid, {
            quantity,
        });
        const oldQuantity = this.salesPosition.quantity;
        this.salesPosition.quantity = quantity;
        if (this.openedDocument.discountType === 'value') {
            await this.discountsAndMultipliersService.calculateDiscountsAndMultipliers(this.openedDocument, this.salesPosition, 'sales');
            this.salesDocumentService.savePositionPrices(
                this.salesPosition.uuid,
                this.openedDocument.uuid,
                {
                    priceTaxRateId: this.salesPosition.priceTaxRateId,
                    priceTaxRate: this.salesPosition.priceTaxRate,
                    priceBeforePromotionsValue: this.salesPosition.priceBeforePromotionsValue,
                    pricePromotions: this.salesPosition.pricePromotions,
                    priceBeforeDiscountsValue: this.salesPosition.priceBeforeDiscountsValue,
                    priceDiscounts: this.salesPosition.priceDiscounts,
                    priceAfterDiscountsValue: this.salesPosition.priceAfterDiscountsValue,
                    priceIsGross: TaxRatesService.isGross(this.openedDocument.taxRateCalculationMethod),
                    priceCurrency: this.salesPosition.priceCurrency,
                },
            );
            if (this.salesPosition.type === 'external_venetian') {
                this.documentManagerService.savePosition(this.salesPosition.uuid, this.openedDocument.uuid, {
                    configuration: this.salesPosition.configuration,
                });
            }
        }
        await this.documentSlavePositions.updateSlavePositionQuantity(this.salesPosition, this.openedDocument, oldQuantity);
        if (this.configService?.user?.offerRecalculationMethod === 'wholeOffer') {
            this.documentPricesService.recalculatePosition(this.openedDocument);
        }
    }

    changePositionName(name: any) {
        if (name && typeof name === 'object' || !name) {
           name = '';
        }
        this.salesDocumentService.savePosition(this.salesPosition.uuid, this.openedDocument.uuid, {
            name,
        });
    }

    async checkNeedChangeUnit(){

        if (this.salesPosition && this.hasAccessTo.unit && this.activePriceVersion?.id) {
            const activePrices = (await this.pricesDataService.getProductPricesForPriceVersion(this.activePriceVersion?.id, this.document?.buyerContractorId));
            this.matchingPrices = activePrices[this.salesPosition.configuration?.productId]
                ?.priceUnits;
            this.editUnit = this.salesPosition.isInvalid && this.errorInPosition('BadUnit') && this.salesPosition?.hasMatchingPrices;
            this.newUnit = null;
            this.newQuantity = this.salesPosition.quantity;
            this.oldQuantity = this.salesPosition.quantity;
        }

    }

    handleNewUnitChange(unitIdCode: string) {
        const [unitId, unitCode] = unitIdCode.split(':');
        if(unitId || unitCode){
            this.newUnit = this.matchingPrices.find(unit => unit.id === +unitId && unit.code === unitCode);
        }
    }

    errorInPosition(errorType) {
        if (this.salesPosition?.errors) {
            if (typeof this.salesPosition.errors === 'string') {
                this.salesPosition.errors = JSON.parse(this.salesPosition?.errors);
            }
            return this.salesPosition?.errors?.filter((error) => error?.type === errorType);
        }
        return false;
    }
    changePositionDescription(description: string) {
        this.salesDocumentService.savePosition(this.salesPosition.uuid, this.openedDocument.uuid, {
            description,
        });
    }

    async removePosition() {
        await this.salesDocumentService.removePosition(this.salesPosition, this.openedDocument);
    }

    duplicatePosition() {
        this.salesDocumentService.duplicatePosition(this.salesPosition, this.openedDocument);
    }

    copyPositionToOtherDocument() {
        this.salesDocumentService.copyPositionToOtherDocument(this.salesPosition, this.openedDocument);
    }

    isFieldHiddenInSummary(field: ComplexConfiguratorField) {
        return typeof field.currentHiddenInSummary === 'boolean'
            ? field.currentHiddenInSummary
            : field.hiddenInSummary;
    }

    changePositionTaxRate(taxRateId: string) {
        this.salesDocumentService.changePositionTaxRate(
            this.salesPosition,
            this.openedDocument,
            Number(taxRateId)
        );
    }

    openPositionPricesDialog() {
        this.dialogService.open(PositionPricesDialogComponent, {
            data: {
                salesDocument: this.openedDocument,
                position: this.salesPosition,
                buyPosition: this.buyPosition,
                currency: this.currency,
                saleView: this.saleView,
                purchaseView: this.purchaseView,
                viewMode: this.viewMode,
                isGross: this.isGross,
                isEditable: this.hasAccessTo.positionPrices,
                taxRates: this.taxRates,
                name: this.positionTitle
            },
        });
    }

    summarizePrices() {
        this.hasPrice = this.position.priceBeforeDiscountsValue !== null &&
            !Number.isNaN(this.position.priceBeforeDiscountsValue) &&
            typeof this.position.priceBeforeDiscountsValue !== 'undefined';

        this.hasBuyPrice = this.buyPosition?.priceBeforeDiscountsValue !== null &&
            !Number.isNaN(this.buyPosition?.priceBeforeDiscountsValue) &&
            typeof this.buyPosition?.priceBeforeDiscountsValue !== 'undefined';

        if (this.position.type !== 'external_venetian') {
            this.priceBase = this.convertToNetSalesPrice(this.position.priceBeforeDiscountsValue);
            this.priceBaseBeforePromotion = this.convertToNetSalesPrice(this.position.priceBeforePromotionsValue);
            this.priceBasePromotion = this.getPromotionPercent(this.priceBaseBeforePromotion, this.priceBase);
            this.buyPriceBase = this.convertToNetBuyPrice(this.buyPosition?.priceBeforeDiscountsValue);
            this.buyPriceBaseBeforePromotion = this.convertToNetBuyPrice(this.buyPosition?.priceBeforePromotionsValue);
            this.buyPriceBasePromotion = this.getPromotionPercent(this.buyPriceBaseBeforePromotion, this.buyPriceBase);
        } else {
            if (this.position.configuration) {
                this.position.configuration = core.parseJson(this.position.configuration);
            }
            if (this.buyPosition?.configuration) {
                this.buyPosition.configuration = core.parseJson(this.buyPosition.configuration);
            }
            if (this.position.configuration?.priceSegments) {
                this.priceBase = this.convertToNetSalesPrice(
                    sumPriceFromSegments(
                        this.position.configuration.priceSegments,
                        this.position.configuration.priceParts, [
                        {
                            value: {
                                type: 'externalVenetianRaster'
                            },
                            percent: {
                                type: null
                            },
                        }
                    ]) +
                    sumPriceFromSegments(
                        this.position.configuration.priceSegments,
                        this.position.configuration.priceParts, [
                        {
                            value: {
                                type: 'externalVenetianRaster'
                            },
                            percent: {
                                type: 'market',
                            },
                        }
                    ]) +
                    sumPriceFromSegments(
                        this.position.configuration.priceSegments,
                        this.position.configuration.priceParts, [
                        {
                            value: {
                                type: 'externalVenetianRaster'
                            },
                            percent: {
                                type: 'charge'
                            },
                        }
                    ])
                );
                this.priceBaseBeforePromotion = this.convertToNetSalesPrice(
                    sumPriceFromSegments(
                        this.position.configuration.priceSegments,
                        this.position.configuration.priceParts, [
                            {
                                value: {
                                    type: 'externalVenetianRaster'
                                },
                                percent: {
                                    type: null
                                },
                            }
                        ],
                        true
                    ) +
                    sumPriceFromSegments(
                        this.position.configuration.priceSegments,
                        this.position.configuration.priceParts, [
                            {
                                value: {
                                    type: 'externalVenetianRaster'
                                },
                                percent: {
                                    type: 'market',
                                },
                            }
                        ],
                        true
                    ) +
                    sumPriceFromSegments(
                        this.position.configuration.priceSegments,
                        this.position.configuration.priceParts, [
                            {
                                value: {
                                    type: 'externalVenetianRaster'
                                },
                                percent: {
                                    type: 'charge'
                                },
                            }
                        ],
                        true
                    )
                );
                this.priceBasePromotion = this.getPromotionPercent(this.priceBaseBeforePromotion, this.priceBase);
                if (this.priceBase !== null && this.hasPrice) {
                    this.priceSurcharges = this.convertToNetSalesPrice(this.position.priceBeforeDiscountsValue) - this.priceBase;
                } else {
                    this.priceSurcharges = null;
                }
                if (this.priceBaseBeforePromotion !== null && this.hasPrice) {
                    this.priceSurchargesBeforePromotion = this.convertToNetSalesPrice(this.position.priceBeforePromotionsValue) - this.priceBaseBeforePromotion;
                } else {
                    this.priceSurchargesBeforePromotion = null
                }
                this.priceSurchargesPromotion = this.getPromotionPercent(this.priceSurchargesBeforePromotion, this.priceSurcharges);
            }
            if (this.buyPosition?.configuration?.priceSegments) {
                this.buyPriceBase = this.convertToNetBuyPrice(
                    sumPriceFromSegments(
                        this.buyPosition.configuration.priceSegments,
                        this.buyPosition.configuration.priceParts, [
                        {
                            value: {
                                type: 'externalVenetianRaster'
                            },
                            percent: {
                                type: null
                            },
                        }
                    ]) +
                    sumPriceFromSegments(
                        this.buyPosition.configuration.priceSegments,
                        this.buyPosition.configuration.priceParts, [
                        {
                            value: {
                                type: 'externalVenetianRaster'
                            },
                            percent: {
                                type: 'market',
                            },
                        }
                    ]) +
                    sumPriceFromSegments(
                        this.buyPosition.configuration.priceSegments,
                        this.buyPosition.configuration.priceParts, [
                        {
                            value: {
                                type: 'externalVenetianRaster'
                            },
                            percent: {
                                type: 'charge'
                            },
                        }
                    ])
                );
                this.buyPriceBaseBeforePromotion = this.convertToNetBuyPrice(
                    sumPriceFromSegments(
                        this.buyPosition.configuration.priceSegments,
                        this.buyPosition.configuration.priceParts, [
                            {
                                value: {
                                    type: 'externalVenetianRaster'
                                },
                                percent: {
                                    type: null
                                },
                            }
                        ],
                        true
                    ) +
                    sumPriceFromSegments(
                        this.buyPosition.configuration.priceSegments,
                        this.buyPosition.configuration.priceParts, [
                            {
                                value: {
                                    type: 'externalVenetianRaster'
                                },
                                percent: {
                                    type: 'market',
                                },
                            }
                        ],
                        true
                    ) +
                    sumPriceFromSegments(
                        this.buyPosition.configuration.priceSegments,
                        this.buyPosition.configuration.priceParts, [
                            {
                                value: {
                                    type: 'externalVenetianRaster'
                                },
                                percent: {
                                    type: 'charge'
                                },
                            }
                        ],
                        true
                    )
                );
                this.buyPriceBasePromotion = this.getPromotionPercent(this.buyPriceBaseBeforePromotion, this.buyPriceBase);
                if (this.buyPriceBase !== null && this.hasBuyPrice) {
                    this.buyPriceSurcharges = this.convertToNetBuyPrice(this.buyPosition.priceBeforeDiscountsValue) - this.buyPriceBase;
                } else {
                    this.buyPriceSurcharges = null;
                }

                if (this.buyPriceBaseBeforePromotion !== null && this.hasBuyPrice) {
                    this.buyPriceSurchargesBeforePromotion = this.convertToNetBuyPrice(this.buyPosition.priceBeforePromotionsValue) - this.buyPriceBaseBeforePromotion;
                } else {
                    this.buyPriceSurchargesBeforePromotion = null;
                }

                this.buyPriceSurchargesPromotion = this.getPromotionPercent(this.buyPriceSurchargesBeforePromotion, this.buyPriceSurcharges);
            }
        }

        this.buyDiscountValue = this.buyPosition?.priceDiscounts?.reduce(
            (prev, curr) => prev + this.convertToNetBuyPrice(curr.value),
            0
        );
        this.buyDiscountPercent =
            this.buyDiscountValue / (this.convertToNetBuyPrice(this.buyPosition?.priceBeforeDiscountsValue) * (this.document?.discountType === 'price' ? 1 : this.buyPosition?.quantity));
        this.discountValue = this.position.priceDiscounts.reduce(
            (prev, curr) => prev + this.convertToNetSalesPrice(curr.value),
            0
        );
        this.discountPercent =
            this.discountValue / (this.convertToNetSalesPrice(this.position.priceBeforeDiscountsValue) * (this.document?.discountType === 'price' ? 1 : this.position.quantity));
    }

    /**
     * Musimy obsłużyć przypadek, gdy dokument zakupu jest brutto, a sprzedaży netto.
     * W takim wypadku, aby dało się porównać ceny wszystko musi być netto.
     * Tak samo, gdy jest odwrotnie do porównania wszystkie ceny są netto.
     */
    convertToNetBuyPrice(price: number) {
        if (this.isComparable || !this.buyPosition?.priceIsGross) {
            return price;
        }
        if (!price) {
            return price;
        }
        return TaxRatesService.recalcToNetPrice(price, this.buyPosition?.priceTaxRate);
    }

    /**
     * Musimy obsłużyć przypadek, gdy dokument zakupu jest brutto, a sprzedaży netto.
     * W takim wypadku, aby dało się porównać ceny wszystko musi być netto.
     * Tak samo, gdy jest odwrotnie do porównania wszystkei ceny są netto.
     */
    convertToNetSalesPrice(price: number) {
        if (this.isComparable || !this.position?.priceIsGross) {
            return price;
        }
        if (!price) {
            return price;
        }
        return TaxRatesService.recalcToNetPrice(price, this.position?.priceTaxRate);
    }

    getPromotionPercent(priceBeforePromotion, priceAfterPromotion) {
        return Math.abs(priceBeforePromotion - priceAfterPromotion) > 2000 * Number.EPSILON ? core.round((priceBeforePromotion - priceAfterPromotion) / priceBeforePromotion * 100) : 0;
    }

    filterBySegmentId(array: any[], segmentId) {
        return array.filter(el => el.segmentId === segmentId);
    }

    private parseSketches(){
        if(this.position?.configuration?.sketchMainData){
            this.position.configuration.sketchMainData = core.parseJson(this.position.configuration.sketchMainData);
        }

        if(this.position?.configuration?.sketchAdditionalData?.length){
            for (const sketchIndex in this.position.configuration.sketchAdditionalData) {
                if(this.position.configuration.sketchAdditionalData[sketchIndex]){
                    this.position.configuration.sketchAdditionalData[sketchIndex] = core.parseJson(
                        this.position.configuration.sketchAdditionalData[sketchIndex]
                    );
                }
            }
        }

    }

    addToastWarnQuantity(){
        this.toastService.add({
            title: this.translateService.instant(
                'SALES_DOCUMENT|Błędna ilość'
            ),
            body: this.translateService.instant(
                'SALES_DOCUMENT|wpisz ilość w liczbach całkowitych',
            ),
            severity: 'warn',
        });
    }

    viewBoxDimensions(viewBox) {
        if (!this.viewBox?.originBox || this.viewBox.originBox?.width !== viewBox.originBox?.width || this.viewBox.originBox?.height !== viewBox.originBox?.height) {
            this.viewBox = viewBox;
            this.setPositionThumbnailType();
        }
    }

    showPositionWideDraw() {
        return this.viewBox && this.viewBox.originBox?.width > 6000;
    }

    setPositionThumbnailType() {
        if (!this.viewBox?.originBox || (this.viewBox.originBox?.width <= 5000 && this.viewBox.originBox?.height <= 5000)) {
            if (this.thumbnailDrawType !== 'top-projection') {
                this.thumbnailDrawType = 'normal';
            }
        } else if (this.viewBox.originBox?.width > 5000 && this.viewBox.originBox?.height <= 5000) {
            this.thumbnailDrawType = 'top-projection';
        } else {
            this.thumbnailDrawType = 'text';
        }
    }

    formatUnit(value: number) {
        const dimensionUnit = getUnit({});
        return unitMMFormatter(
            value,
            this.configService.config.config,
            1,
            0,
            dimensionUnit.unit,
            0
        );
    }

    switchShowDetails(showDetails: boolean) {
        this.showDetailsEvent.emit(showDetails);
    }

    isStaticPosition(position: SalesDocumentPosition) {
        return this.staticPositionDetailsService.isStaticPosition(position);
    }

    getWidthInputClass(quantity:number, editUnit = false){
        if(editUnit){
            return 'width-1';
        }else{
            if(quantity<100){
                return 'width-6';
            }else if(quantity<1000){
                return 'width-6-2';
            }else{
                return 'width-6-3';
            }
        }
    }

    getPositionSlaves() {
        const slaves: string[] = [];
        for (const fieldId in this.position.slaves) {
            if (this.position.slaves[fieldId].positionId) {
                slaves.push(`${this.positionsIndexes.get(this.position.slaves[fieldId].positionId)}. ${this.positionsTitles.get(this.position.slaves[fieldId].positionId).join('/')}`);
            }
        }
        return slaves;
    }

    getPositionMasters() {
        const masters: string[] = [];
        for (const masterId in this.position.masters) {
            masters.push(this.positionsIndexes.get(masterId) + '. ' + this.positionsTitles.get(masterId)?.join('/'));
        }
        return masters;
    }

    async canAddSpareParts() {
        if (this.position.type === 'static' || this.position.type === 'additional') {
            return false;
        }
        if (!this.configuratorLoaderService.configuratorsRepositoryServiceStatic) {
            return false;
        }
        try {
            const configuratorId = this.position.type === 'complex' ? this.position.configuration.configuratorId : 0;
            await this.configuratorLoaderService.configuratorsRepositoryService.loadConfigurator(
                this.position.type,
                configuratorId
            );
            return await this.configuratorLoaderService.configuratorsRepositoryServiceStatic.getSpareParts(this.position.type, configuratorId)?.applicable(this.position.configuration);
        } catch (error) {
            logger.error(error);
            return false;
        }
    }

    async addSpareParts() {
        if (!this.openedDocument) {
            return;
        }
        await this.configuratorLoaderService.configuratorModuleRef.instance.loadConfiguratorData(this.salesPosition?.type, 0, 0, this.configService.config.languageCode ?? 'pl', true);
        this.salesDocumentService.copyPositionToOtherDocument(this.salesPosition, this.openedDocument, true);
    }

    changePositionDelivered(delivered: boolean) {
        this.deliveredChange.emit(delivered);
    }

    requestComplaint() {
        this.router.navigate(['complaint-v1', 'complaint', 'new', this.buyPosition.documentId, this.buyPosition.uuid]);
    }

    fixPositionName() {
        if (this.salesPosition?.name && typeof this.salesPosition.name === 'object') {
            this.salesPosition.name = '';
            this.salesDocumentService.savePosition(this.salesPosition.uuid, this.openedDocument.uuid, {
                name: this.salesPosition.name,
            });
        }
    }
}
